<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  knowledgeBase: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const isLoading = ref(false);
const isSaving = ref(false);
const showAddForm = ref(false);
const newQuestion = ref('');
const newAnswer = ref('');
const editingId = ref(null);
const editQuestion = ref('');
const editAnswer = ref('');

const accountId = computed(() => store.getters.getCurrentAccountId);
const sources = computed(() =>
  store.getters['knowledgeBases/getSourcesByKnowledgeBase'](props.knowledgeBase.id)
    .filter(s => s.source_type === 'faq')
);

const statusIcon = status => ({
  pending: 'i-lucide-clock',
  processing: 'i-lucide-loader-circle',
  ready: 'i-lucide-check-circle',
  error: 'i-lucide-x-circle',
}[status] || 'i-lucide-clock');

const statusClass = status => ({
  pending: 'text-n-slate-11',
  processing: 'text-yellow-600 animate-spin',
  ready: 'text-green-600',
  error: 'text-red-600',
}[status] || 'text-n-slate-11');

const addFaq = async () => {
  if (!newQuestion.value.trim() || !newAnswer.value.trim()) return;
  isSaving.value = true;
  try {
    await store.dispatch('knowledgeBases/createSource', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
      data: {
        knowledge_source: {
          name: newQuestion.value.trim().slice(0, 80),
          source_type: 'faq',
          metadata: {
            faq_entries: [
              { question: newQuestion.value.trim(), answer: newAnswer.value.trim() },
            ],
          },
        },
      },
    });
    useAlert(t('KNOWLEDGE_BASE.FAQ.ADD.SUCCESS'));
    newQuestion.value = '';
    newAnswer.value = '';
    showAddForm.value = false;
  } catch {
    useAlert(t('KNOWLEDGE_BASE.FAQ.ADD.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const startEdit = source => {
  editingId.value = source.id;
  editQuestion.value = source.metadata?.faq_entries?.[0]?.question || source.name;
  editAnswer.value = source.metadata?.faq_entries?.[0]?.answer || '';
};

const cancelEdit = () => {
  editingId.value = null;
  editQuestion.value = '';
  editAnswer.value = '';
};

const saveEdit = async source => {
  if (!editQuestion.value.trim() || !editAnswer.value.trim()) return;
  isSaving.value = true;
  try {
    await store.dispatch('knowledgeBases/updateSource', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
      sourceId: source.id,
      data: {
        knowledge_source: {
          name: editQuestion.value.trim().slice(0, 80),
          metadata: {
            faq_entries: [
              { question: editQuestion.value.trim(), answer: editAnswer.value.trim() },
            ],
          },
        },
      },
    });
    useAlert(t('KNOWLEDGE_BASE.FAQ.EDIT.SUCCESS'));
    cancelEdit();
  } catch {
    useAlert(t('KNOWLEDGE_BASE.FAQ.EDIT.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const reprocess = async source => {
  try {
    await store.dispatch('knowledgeBases/reprocessSource', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
      sourceId: source.id,
    });
    useAlert(t('KNOWLEDGE_BASE.FAQ.REPROCESS.SUCCESS'));
  } catch {
    useAlert(t('KNOWLEDGE_BASE.FAQ.REPROCESS.ERROR'));
  }
};

const deleteSource = async source => {
  try {
    await store.dispatch('knowledgeBases/deleteSource', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
      sourceId: source.id,
    });
    useAlert(t('KNOWLEDGE_BASE.FAQ.DELETE.SUCCESS'));
  } catch {
    useAlert(t('KNOWLEDGE_BASE.FAQ.DELETE.ERROR'));
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await store.dispatch('knowledgeBases/getSources', {
      accountId: accountId.value,
      knowledgeBaseId: props.knowledgeBase.id,
    });
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12 m-0">
          {{ $t('KNOWLEDGE_BASE.FAQ.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mt-0.5 m-0">
          {{ $t('KNOWLEDGE_BASE.FAQ.DESCRIPTION') }}
        </p>
      </div>
      <Button
        icon="i-lucide-plus"
        :label="$t('KNOWLEDGE_BASE.FAQ.ADD.BUTTON')"
        size="sm"
        @click="showAddForm = !showAddForm"
      />
    </div>

    <!-- Formulário de adição -->
    <div v-if="showAddForm" class="bg-n-alpha-1 border border-n-weak rounded-lg p-4 space-y-3">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">
          {{ $t('KNOWLEDGE_BASE.FAQ.FORM.QUESTION') }} *
        </label>
        <input
          v-model="newQuestion"
          type="text"
          class="input"
          :placeholder="$t('KNOWLEDGE_BASE.FAQ.FORM.QUESTION_PLACEHOLDER')"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">
          {{ $t('KNOWLEDGE_BASE.FAQ.FORM.ANSWER') }} *
        </label>
        <textarea
          v-model="newAnswer"
          class="input"
          rows="4"
          :placeholder="$t('KNOWLEDGE_BASE.FAQ.FORM.ANSWER_PLACEHOLDER')"
        />
      </div>
      <div class="flex justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          :label="$t('KNOWLEDGE_BASE.FORM.CANCEL')"
          @click="showAddForm = false"
        />
        <Button
          size="sm"
          :label="$t('KNOWLEDGE_BASE.FAQ.ADD.SAVE')"
          :is-loading="isSaving"
          :disabled="!newQuestion.trim() || !newAnswer.trim()"
          @click="addFaq"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <!-- Lista de FAQs -->
    <div v-else-if="sources.length" class="divide-y divide-n-weak">
      <div v-for="source in sources" :key="source.id" class="py-3">
        <!-- Modo edição -->
        <div v-if="editingId === source.id" class="space-y-3">
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              {{ $t('KNOWLEDGE_BASE.FAQ.FORM.QUESTION') }} *
            </label>
            <input
              v-model="editQuestion"
              type="text"
              class="input"
              :placeholder="$t('KNOWLEDGE_BASE.FAQ.FORM.QUESTION_PLACEHOLDER')"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-11">
              {{ $t('KNOWLEDGE_BASE.FAQ.FORM.ANSWER') }} *
            </label>
            <textarea
              v-model="editAnswer"
              class="input"
              rows="4"
              :placeholder="$t('KNOWLEDGE_BASE.FAQ.FORM.ANSWER_PLACEHOLDER')"
            />
          </div>
          <div class="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              :label="$t('KNOWLEDGE_BASE.FORM.CANCEL')"
              @click="cancelEdit"
            />
            <Button
              size="sm"
              :label="$t('KNOWLEDGE_BASE.FAQ.EDIT.SAVE')"
              :is-loading="isSaving"
              :disabled="!editQuestion.trim() || !editAnswer.trim()"
              @click="saveEdit(source)"
            />
          </div>
        </div>

        <!-- Modo visualização -->
        <div v-else class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ source.metadata?.faq_entries?.[0]?.question || source.name }}
              </span>
              <span :class="`i-lucide w-3.5 h-3.5 ${statusIcon(source.status)} ${statusClass(source.status)}`" />
            </div>
            <p v-if="source.metadata?.faq_entries?.[0]?.answer" class="text-xs text-n-slate-11 mt-0.5 m-0 line-clamp-2">
              {{ source.metadata.faq_entries[0].answer }}
            </p>
            <p v-else class="text-xs text-n-slate-11 mt-0.5 m-0">
              {{ $t(`KNOWLEDGE_BASE.SOURCE_STATUS.${source.status?.toUpperCase()}`) }}
            </p>
          </div>
          <div class="flex gap-1 shrink-0">
            <Button
              v-tooltip.top="$t('KNOWLEDGE_BASE.FAQ.EDIT.BUTTON')"
              icon="i-lucide-pencil"
              xs
              slate
              faded
              @click="startEdit(source)"
            />
            <Button
              v-if="source.status === 'error' || source.status === 'pending'"
              v-tooltip.top="$t('KNOWLEDGE_BASE.FAQ.REPROCESS.BUTTON')"
              icon="i-lucide-refresh-cw"
              xs
              slate
              faded
              @click="reprocess(source)"
            />
            <Button
              v-tooltip.top="$t('KNOWLEDGE_BASE.FAQ.DELETE.BUTTON')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              @click="deleteSource(source)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Vazio -->
    <div v-else-if="!isLoading" class="text-center py-10">
      <p class="text-sm text-n-slate-11">{{ $t('KNOWLEDGE_BASE.FAQ.EMPTY') }}</p>
    </div>
  </div>
</template>
